import SavingsIcon from '@mui/icons-material/Savings';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Adsense from '../Ads/Adsense';
import { logEvent } from '../../utils/ga';
import { PageHelmet } from '../shared/PageHelmet/PageHelmet';
import styles from './HomePage.module.css';

const CHART_DATA = [
  { period: '10년', rate3: 4191, rate6: 4917, rate9: 5839 },
  { period: '20년', rate3: 9862, rate6: 13934, rate9: 20041 },
  { period: '30년', rate3: 17487, rate6: 30115, rate9: 54774 },
];

const tools = [
  {
    to: '/compound',
    Icon: TrendingUpIcon,
    title: '복리 시뮬레이터',
    desc: '초기 투자금과 월 납입액이 20년 뒤 얼마가 되는지 연도별로 확인하세요.',
    tag: '가장 많이 사용',
  },
  {
    to: '/retirement',
    Icon: SavingsIcon,
    title: '노후 자금 계산기',
    desc: '모아둔 자산이 몇 살까지 버티는지 계산합니다.',
  },
  {
    to: '/retirement/goal',
    Icon: TrackChangesIcon,
    title: '목표 설계 계산기',
    desc: '은퇴 목표 자산이 정해졌다면, 지금 매달 얼마를 모아야 하는지 역산합니다.',
  },
];

const additionalTools = [
  { to: '/pension', label: '국민연금 계산기', desc: '예상 월 연금액과 소득 대체율 확인' },
  { to: '/dividend', label: '배당 ETF 계산기', desc: '월 배당금 시뮬레이션 + 8개 ETF 비교' },
  { to: '/tax-benefit', label: 'ISA·IRP 절세 계산기', desc: '연간 세제혜택 합산 계산' },
  { to: '/inflation', label: '물가 상승 계산기', desc: '인플레이션 반영 실질 필요 자금' },
  { to: '/severance', label: '퇴직금 계산기', desc: '세전·세후 퇴직금과 추정 세금 확인' },
  { to: '/fire', label: 'FIRE 계산기', desc: '조기 은퇴 목표 자산과 달성 예상 나이 계산' },
  { to: '/checklist', label: '은퇴 준비 체크리스트', desc: '단계별 노후 준비 점검' },
  { to: '/quiz', label: '노후 준비 진단 퀴즈', desc: '5가지 질문으로 내 노후 준비 점수 확인' },
];

const compareItems = [
  { href: '/compare/isa-vs-irp', label: 'ISA vs IRP — 어떤 계좌가 유리할까?' },
  { href: '/compare/schd-vs-jepi', label: 'SCHD vs JEPI — 어떤 배당 ETF가 좋을까?' },
  { href: '/scenario/fire-30s', label: '30대 FIRE 달성 가능한 나이는?' },
  { href: '/scenario/pension-at-65', label: '65세 은퇴 시 국민연금 예상액' },
  { href: '/scenario/severance-10years', label: '10년 근무 퇴직금 계산' },
  { href: '/compare/national-pension-vs-private', label: '국민연금 vs 개인연금 비교' },
];

export const HomePage = () => {
  return (
    <div className={styles.root}>
      <PageHelmet
        path="/"
        title="노후든든 — 복리 계산기, 노후 자금 시뮬레이터"
        description="복리 시뮬레이터와 노후 자금 계산기로 나의 은퇴 시나리오를 직접 설계하세요. 지금 저축 습관이 노후를 바꿉니다."
      />

      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <span className={styles.heroLabel}>노후 준비 플랫폼</span>
          <h1 className={styles.heroH1}>지금의 선택이<br />노후를 만듭니다</h1>
          <p className={styles.heroSubtitle}>
            숫자로 확인하면 행동이 달라집니다. 복리 시뮬레이터와 노후 자금 계산기로
            나의 은퇴 시나리오를 직접 설계해보세요.
          </p>
          <div className={styles.heroCTAs}>
            <Link
              to="/compound"
              className={styles.ctaPrimary}
              onClick={() => logEvent('engagement', 'home_hero_cta_click', '/compound')}
            >
              내 은퇴 시나리오 계산하기
            </Link>
            <Link
              to="/guide"
              className={styles.ctaSecondary}
              onClick={() => logEvent('engagement', 'home_hero_cta_click', '/guide')}
            >
              투자 가이드 읽기 →
            </Link>
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className={styles.toolsSection}>
        <div className={styles.sectionInner}>
          <p className={styles.sectionLabel}>계산기</p>
          <h2 className={styles.sectionH2}>노후 준비에 필요한 3가지 도구</h2>
          <p className={styles.sectionDesc}>
            한 번 입력하면 바로 결과가 나옵니다. 복잡한 금융 지식 없이도 나의 상황을 숫자로 파악할 수 있습니다.
          </p>
          <div className={styles.toolGrid}>
            {tools.map(({ to, Icon, title, desc, tag }) => (
              <Link
                key={to}
                to={to}
                className={styles.toolCard}
                onClick={() => logEvent('engagement', 'home_tool_card_click', to)}
              >
                {tag && <span className={styles.toolCardTag}>{tag}</span>}
                <div className={styles.toolCardIcon}>
                  <Icon fontSize="small" />
                </div>
                <h3 className={styles.toolCardTitle}>{title}</h3>
                <p className={styles.toolCardDesc}>{desc}</p>
                <span className={styles.toolCardArrow}>시작하기 →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div className={styles.additionalToolsSection}>
        <p className={styles.additionalToolsLabel}>더 많은 도구</p>
        <div className={styles.additionalToolsGrid}>
          {additionalTools.map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className={styles.additionalToolCard}
              onClick={() => logEvent('engagement', 'home_additional_tool_click', to)}
            >
              <span className={styles.additionalToolLabel}>{label}</span>
              <span className={styles.additionalToolDesc}>{desc}</span>
              <span className={styles.additionalToolArrow}>→</span>
            </Link>
          ))}
        </div>
      </div>

      <section className={styles.compareSection}>
        <div className={styles.sectionInner}>
          <h2 className={styles.compareSectionTitle}>자주 찾는 질문</h2>
          <div className={styles.compareGrid}>
            {compareItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={styles.compareCard}
                onClick={() => logEvent('home', 'scenario_card_click', item.href)}
              >
                {item.label} →
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Compound power */}
      <section className={styles.compoundSection}>
        <div className={styles.sectionInner}>
          <div className={styles.compoundGrid}>
            <div className={styles.compoundText}>
              <p className={styles.sectionLabel}>복리의 힘</p>
              <h2 className={styles.compoundH2}>
                72의 법칙 — 연 7% 수익률이면<br />약 10년마다 자산이 두 배가 됩니다
              </h2>
              <p className={styles.compoundBody}>
                원금 5,000만원을 연 7%로 운용하면 10년 후 약 9,836만원, 20년 후 약 1억 9,348만원이 됩니다.
                단순한 이자가 아닌 수익에 수익이 쌓이는 복리의 힘 때문입니다.
              </p>
              <p className={styles.compoundBody}>
                그래프는 매월 30만원을 납입할 때 수익률 차이가 30년 후 어떤 결과를 만드는지 보여줍니다.
                수익률 3% vs 9%의 차이는 3배 이상입니다.
              </p>
              <p className={styles.compoundNote}>* 세금·수수료 미반영. 참고용 시뮬레이션.</p>
            </div>
            <div>
              <div className={styles.chartWrapper}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={CHART_DATA} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="period" axisLine={false} tickLine={false} style={{ fontSize: '0.8125rem' }} />
                    <YAxis
                      tickFormatter={(v) => `${(v / 10000).toFixed(0)}억`}
                      axisLine={false}
                      tickLine={false}
                      style={{ fontSize: '0.75rem' }}
                    />
                    <Tooltip
                      formatter={((value: number) => [`${value.toLocaleString('ko-KR')}만원`]) as any}
                    />
                    <Bar dataKey="rate3" name="연 3%" fill="#9ca3af" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="rate6" name="연 6%" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="rate9" name="연 9%" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className={styles.chartLegend}>
                {[
                  { color: '#9ca3af', label: '연 3%' },
                  { color: '#3b82f6', label: '연 6%' },
                  { color: '#10b981', label: '연 9%' },
                ].map(({ color, label }) => (
                  <span key={label} className={styles.legendItem}>
                    <span className={styles.legendDot} style={{ background: color }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className={styles.ctaBanner}>
        <h2 className={styles.ctaBannerH2}>지금 시작하는 것이 10년 후를 바꿉니다</h2>
        <p className={styles.ctaBannerBody}>
          5분이면 나의 노후 시나리오를 확인할 수 있습니다.
        </p>
        <Link
          to="/compound"
          className={styles.ctaBannerBtn}
          onClick={() => logEvent('engagement', 'home_bottom_cta_click', '/compound')}
        >
          복리 시뮬레이터 시작하기
        </Link>
      </section>

      <Adsense adClient="ca-pub-9359050149154028" adSlot="6979626644" />

      <p className={styles.disclaimer}>
        이 사이트는 투자 권유가 아닌 참고용 시뮬레이션입니다. 실제 수익은 시장 상황에 따라 다르며,
        세금·수수료·물가 상승률은 반영되지 않았습니다. 중요한 재무 결정은 전문가와 상담하세요.
      </p>
    </div>
  );
};
